"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PenIcon, Loader2 } from "lucide-react"
import ProductServiceService from "@/api/productservices.service"
import { useToast } from "@/hooks/use-toast"

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().max(255, "Description must be less than 255 characters"),
  unitPrice: z.number().min(0, "Price must be 0 or greater"),
  currency: z.string().length(3, "Currency must be a 3-letter code")
})

interface EditProductServiceFormProps {
  productService: any;
}

export function EditProductServiceForm({ productService }: EditProductServiceFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: productService,
  })

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      const response = await ProductServiceService.updateSpecificProductService(productService.publicId, values);
      const result = response.data;
      if (result.result === "success") {
        toast({
          title: "Success 🎉",
          description: "Product/Service updated successfully",
        });
        setTimeout(() => {
          location.reload();
        }, 3000);
      } else {
        toast({
          title: "Error ❌",
          description: result.message || "Failed to update product/service",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error ❌",
        description: `${error}`
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Product/Service Name" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the product or service name.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea placeholder="Describe the product or service" {...field} />
                </FormControl>
                <FormDescription>
                  Enter a detailed description.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unitPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit Price</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="0" 
                    {...field}
                    onChange={e => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Enter the price per unit.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      {/* Add more currencies as needed */}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Select the currency.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

        </div>

        <div className="flex justify-end">
          <Button type="submit" className="bg-[#0E415E] hover:bg-[#1F628D] text-white">
            {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</> : <><PenIcon className="w-4 h-4 mr-2" /> Update Product/Service</>}
          </Button>
        </div>
      </form>
    </Form>
  )
}
